/**
 * Saying no to a delete, in a sentence somebody can act on.
 *
 * ## Why this is a module and not four `if` statements
 *
 * Every record in this system is attached to others, and the attachments are
 * the whole point: an invoice exists because of a job, a job because of a
 * customer, a payment because of an invoice. So "delete" is nearly always a
 * question about what else goes, and the answer is nearly always no.
 *
 * A "no" is only useful if it says what is in the way and what to do instead.
 * "Cannot delete customer" tells somebody they are stuck. "This customer has
 * 4 jobs and 2 invoices — delete those first, or keep the record" tells them
 * where to go. The difference is the whole value of the feature, and it is
 * the part that rots first when four screens each write their own wording.
 *
 * So the counting stays on the server, where the database is, and the
 * sentence is built here — dependency-free, testable without a database, and
 * identical whether the refusal came from deleting one record or from a row
 * in a bulk delete of two hundred.
 */
export function blocker(count, one, many) {
    return { count, one, many };
}
function phrase(b) {
    return `${b.count} ${b.count === 1 ? b.one : b.many}`;
}
/** "4 jobs", "4 jobs and 2 invoices", "4 jobs, 2 invoices and a payment". */
function list(parts) {
    if (parts.length === 1)
        return parts[0];
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
/**
 * The refusal, or null when nothing is in the way.
 *
 * `advice` is the half that matters: without it this is a complaint. It is
 * passed in rather than derived because what to do instead differs per
 * record — a job gets cancelled, an invoice gets voided, a customer just
 * stays on file.
 */
export function deletionRefusal(subject, blockers, advice) {
    const real = blockers.filter((b) => b.count > 0);
    if (real.length === 0)
        return null;
    return `This ${subject} has ${list(real.map(phrase))}. ${advice}`;
}
/**
 * What to put on the confirm button, and in the sentence above it.
 *
 * Counting is the one thing a confirmation must get right: somebody who
 * selected two hundred rows and is about to lose them needs the number in
 * front of them, not "the selected items".
 */
export function deletionPrompt(count, one, many) {
    return count === 1 ? `Delete this ${one}?` : `Delete ${count} ${many}?`;
}
//# sourceMappingURL=deletion.js.map